import {
  CameraIcon,
  CameraOffIcon,
  MicIcon,
  MicOffIcon,
  MonitorIcon,
  PauseIcon,
  PlayIcon,
  RecordIcon,
  RestartIcon,
  StopIcon,
  WaveformIcon,
} from '../components/icons';
import { Button, Kbd, Toggle, cx } from '../components/ui';
import { bytes, clock } from '../lib/format';
import type { RecordingMode } from './codec';
import { LONG_RECORDING_MS, type RecorderApi } from './useRecorder';

/**
 * Mode and source toggles before recording; transport controls during it.
 *
 * Pause, resume and restart are first-class here — same size, same row, always
 * visible, each with a shortcut printed next to it. They are not hidden behind
 * a menu, because the whole reason this product exists is that pausing a
 * recording should never cost you the take.
 */

const MODES: ReadonlyArray<{ value: RecordingMode; label: string; hint: string }> = [
  { value: 'screen', label: 'Screen', hint: 'Record a screen, window, or tab' },
  { value: 'audio', label: 'Audio only', hint: 'Record just your voice — no picture' },
];

export function ModePicker({
  mode,
  onChange,
  disabled,
  screenAvailable,
  audioAvailable,
}: {
  mode: RecordingMode;
  onChange: (mode: RecordingMode) => void;
  disabled: boolean;
  screenAvailable: boolean;
  audioAvailable: boolean;
}) {
  // Nothing to choose between: don't offer a choice.
  if (!screenAvailable || !audioAvailable) return null;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div
        role="radiogroup"
        aria-label="What to record"
        className="inline-flex rounded border border-hairline bg-slate-850 p-0.5"
      >
        {MODES.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={mode === option.value}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            title={disabled ? 'Finish or discard this take to change what you are recording' : option.hint}
            className={cx(
              'flex items-center gap-2 rounded-xs px-3 py-1.5 text-body transition-colors duration-120',
              'disabled:opacity-40 disabled:pointer-events-none',
              mode === option.value ? 'bg-slate-700 text-chalk' : 'text-chalk-dim hover:text-chalk',
            )}
          >
            <span aria-hidden="true">
              {option.value === 'screen' ? <MonitorIcon size={14} /> : <WaveformIcon size={14} />}
            </span>
            {option.label}
          </button>
        ))}
      </div>
      <span className="text-caption text-chalk-faint">
        {mode === 'audio'
          ? 'A voice note: microphone only, no screen prompt.'
          : 'Your screen, with your voice and webcam if you want them.'}
      </span>
    </div>
  );
}

export function SourceBar({ recorder }: { recorder: RecorderApi }) {
  const busy = recorder.phase === 'recording' || recorder.phase === 'paused' || recorder.phase === 'countdown';

  // Audio-only has exactly one source, and it is mandatory. Offering a mic
  // *toggle* would imply it could be turned off, which would leave nothing.
  if (recorder.mode === 'audio') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Toggle
          active={recorder.hasMic}
          onChange={() => void recorder.arm()}
          disabled={busy || recorder.phase === 'arming'}
          icon={recorder.hasMic ? <MicIcon size={15} /> : <MicOffIcon size={15} />}
          label={recorder.hasMic ? 'Change microphone' : 'Use my microphone'}
          title={
            busy
              ? 'The microphone is locked once recording starts — restart to change it'
              : recorder.hasMic
                ? 'Pick a different microphone'
                : 'Slate needs your microphone to record a voice note'
          }
        />
        <span className="text-caption text-chalk-faint">
          {recorder.hasMic
            ? 'Ready when you are.'
            : 'Allow the microphone to enable the record button.'}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Toggle
        active={recorder.hasDisplay}
        onChange={() => void recorder.arm()}
        disabled={busy}
        icon={<MonitorIcon size={15} />}
        label={recorder.hasDisplay ? 'Change screen' : 'Choose screen'}
        title={
          recorder.hasDisplay
            ? 'Pick a different screen, window, or tab'
            : 'Pick a screen, window, or tab to record'
        }
      />
      <Toggle
        active={recorder.hasMic}
        onChange={() => void recorder.toggleMic()}
        disabled={busy}
        icon={recorder.hasMic ? <MicIcon size={15} /> : <MicOffIcon size={15} />}
        label="Microphone"
        title={busy ? 'Sources are locked once recording starts — restart to change them' : 'Record your voice'}
      />
      <Toggle
        active={recorder.hasCamera}
        onChange={() => void recorder.toggleCamera()}
        disabled={busy || !recorder.support.canvasCapture}
        icon={recorder.hasCamera ? <CameraIcon size={15} /> : <CameraOffIcon size={15} />}
        label="Webcam"
        title={
          busy
            ? 'Sources are locked once recording starts — restart to change them'
            : 'Burn a webcam bubble into the recording'
        }
      />
    </div>
  );
}

function UploadStatus({ recorder }: { recorder: RecorderApi }) {
  const { upload } = recorder;
  if (upload.phase === 'idle') return null;

  const percent =
    upload.capturedBytes > 0
      ? Math.min(100, Math.round((upload.uploadedBytes / upload.capturedBytes) * 100))
      : 0;

  return (
    <div className="flex items-center gap-2 text-caption text-chalk-faint">
      <span
        className={cx(
          'h-1.5 w-1.5 rounded-full',
          upload.error ? 'bg-danger' : upload.phase === 'done' ? 'bg-go' : 'bg-signal',
        )}
        aria-hidden="true"
      />
      <span className="tnum">
        {upload.error
          ? 'Upload stalled — it will be retried'
          : upload.phase === 'done'
            ? 'Uploaded'
            : `Uploading as you record · ${percent}% of ${bytes(upload.capturedBytes)}`}
      </span>
    </div>
  );
}

export function TransportBar({
  recorder,
  onCancel,
}: {
  recorder: RecorderApi;
  onCancel?: () => void;
}) {
  const { phase } = recorder;
  const live = phase === 'recording' || phase === 'paused';
  const tooLong = recorder.elapsedMs > LONG_RECORDING_MS;

  if (phase === 'idle' || phase === 'arming' || phase === 'ready' || phase === 'countdown') {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="record"
            size="lg"
            className="rounded-full pl-4"
            disabled={!recorder.armed || phase === 'arming' || phase === 'countdown'}
            busy={phase === 'arming'}
            onClick={recorder.start}
            title={
              recorder.armed
                ? undefined
                : recorder.mode === 'audio'
                  ? 'Allow the microphone first — a voice note cannot be silent'
                  : 'Pick a screen, window, or tab first'
            }
          >
            {phase !== 'arming' ? <RecordIcon size={12} /> : null}
            {phase === 'countdown' ? 'Starting…' : 'Start recording'}
          </Button>
          {recorder.armed && phase === 'ready' ? (
            <span className="hidden items-center gap-1.5 text-caption text-chalk-faint sm:flex">
              or press <Kbd>Space</Kbd>
            </span>
          ) : null}
        </div>
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    );
  }

  if (phase === 'finishing') {
    return (
      <div className="flex items-center gap-2 text-body text-chalk-dim">
        <Button variant="secondary" size="lg" busy disabled>
          Finishing up
        </Button>
        <span className="text-caption text-chalk-faint">
          Flushing the last few seconds to storage.
        </span>
      </div>
    );
  }

  if (!live) return null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 rounded-full border border-hairline bg-slate-850 px-3 py-1.5">
          <span
            className={cx(
              'h-2.5 w-2.5 rounded-full',
              phase === 'recording' ? 'animate-record-pulse bg-record' : 'bg-chalk-faint',
            )}
            aria-hidden="true"
          />
          <span className="slate-type text-body text-chalk" data-timecode>
            {clock(recorder.elapsedMs / 1000)}
          </span>
        </div>

        <Button
          variant="secondary"
          size="lg"
          onClick={recorder.togglePause}
          title={phase === 'recording' ? 'Pause (Space)' : 'Resume (Space)'}
        >
          {phase === 'recording' ? <PauseIcon size={15} /> : <PlayIcon size={15} />}
          {phase === 'recording' ? 'Pause' : 'Resume'}
        </Button>

        <Button
          variant="secondary"
          size="lg"
          onClick={recorder.restart}
          title={
            recorder.mode === 'audio'
              ? 'Discard this take and start again — your microphone stays open (Shift+R)'
              : 'Discard this take and start again — your screen stays shared (Shift+R)'
          }
        >
          <RestartIcon size={15} />
          Restart
        </Button>

        <Button variant="primary" size="lg" onClick={() => void recorder.stop()} title="Finish (Enter)">
          <StopIcon size={13} />
          Finish
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <UploadStatus recorder={recorder} />
        <span className="hidden items-center gap-1.5 text-caption text-chalk-faint sm:flex">
          <Kbd>Space</Kbd> pause
          <Kbd>Shift</Kbd>
          <Kbd>R</Kbd> restart
          <Kbd>Enter</Kbd> finish
        </span>
      </div>

      {tooLong ? (
        <p className="text-caption text-caution">
          This take is over 55 minutes. Long recordings hold a lot of{' '}
          {recorder.mode === 'audio' ? 'audio' : 'video'} in memory — consider finishing and
          starting a fresh one.
        </p>
      ) : null}
    </div>
  );
}
